import type { ReactElement } from 'react'
import Link from 'next/link'
import { NUTRITION_GOALS } from '../constants'
import type { CalculationResult } from '../lib/formula'

/**
 * Presentational results view (props-only — state lives in CalculatorForm, T5).
 * Where brand-safe conversion happens: estimate + macros in empowering non-diet
 * «ти» copy (AC-01), floor-clamped lower-bound copy (AC-04), a limitations
 * disclaimer with ≥2 Ration-only factors (AC-08), and the Individual Ration CTA
 * (AC-05). Built from existing design-system tokens — no new styling, no UI lib.
 */

const content = {
  heading: 'Твій орієнтир',
  intro: 'Це приблизні значення для твого дня — орієнтир, а не припис. Реальні потреби залежать від багатьох деталей, які формула не бачить.',
  goalPrefix: 'Ціль:',
  energyLabel: 'Орієнтовна енергія на день',
  energyUnit: 'ккал',
  clampedNote: 'Це нижня орієнтовна межа — не ціль і не обмеження. Твій організм заслуговує достатньо енергії щодня.',
  macrosHeading: 'Приблизний розподіл БЖВ',
  macros: {
    protein: 'Білки',
    fat: 'Жири',
    carb: 'Вуглеводи',
  },
  macrosUnit: 'г',
  limitationsHeading: 'Що варто памʼятати',
  limitationsLead: 'Ці цифри — інформаційні, це не медична порада й не заміна консультації.',
  limitationsFactorsLead: 'Індивідуальний Раціон враховує те, що калькулятор не може:',
  limitationsFactors: [
    'твої смаки, улюблені та небажані продукти',
    'твій графік, режим дня та ритм тренувань',
    'самопочуття, звички й особливості твого організму',
  ],
  ctaHeading: 'Готова до плану саме під тебе?',
  ctaBody: 'Індивідуальний Раціон — це меню без заборон під твій смак і графік, з місяцем супроводу. 3 000 грн.',
  ctaLabel: 'Замовити раціон',
} as const

interface CalculatorResultsProps {
  result: CalculationResult
}

export function CalculatorResults({ result }: CalculatorResultsProps): ReactElement {
  const t = content
  const goal = NUTRITION_GOALS.find((g) => g.id === result.goal)

  const macroItems = [
    { key: 'protein', label: t.macros.protein, value: result.macros.protein },
    { key: 'fat', label: t.macros.fat, value: result.macros.fat },
    { key: 'carb', label: t.macros.carb, value: result.macros.carb },
  ] as const

  return (
    <section
      aria-label="Результат розрахунку"
      className="mt-8 md:mt-10 space-y-6"
    >
      <div className="animate-fade-in-up bg-white rounded-2xl md:rounded-3xl shadow-xl shadow-purple/5 border border-rose/10 p-6 md:p-10">
        <h2 className="font-alumni text-3xl md:text-4xl font-bold text-deep">{t.heading}</h2>
        {goal && (
          <p className="mt-1 font-montserrat-alt text-sm text-mauve">
            {t.goalPrefix} {goal.label}
          </p>
        )}
        <p className="mt-4 font-montserrat-alt text-sm md:text-base text-deep/80 leading-relaxed max-w-2xl">
          {t.intro}
        </p>

        <div className="mt-6 rounded-2xl bg-gradient-hero text-cream p-6 md:p-8">
          <p className="font-montserrat-alt text-xs md:text-sm uppercase tracking-[0.2em] text-cream/70">
            {t.energyLabel}
          </p>
          <p className="mt-2 font-alumni font-bold leading-none">
            <span className="text-5xl md:text-7xl">{result.energy.toLocaleString('uk-UA')}</span>{' '}
            <span className="text-2xl md:text-3xl text-cream/80">{t.energyUnit}</span>
          </p>
          {result.clamped && (
            <p className="mt-3 font-montserrat-alt text-xs md:text-sm text-cream/90 leading-relaxed max-w-xl">
              {t.clampedNote}
            </p>
          )}
        </div>

        <div className="mt-6">
          <h3 className="font-alumni text-xl md:text-2xl font-semibold text-deep">{t.macrosHeading}</h3>
          <dl className="mt-3 grid grid-cols-3 gap-3 md:gap-4">
            {macroItems.map((item, index) => (
              <div
                key={item.key}
                style={{ animationDelay: `${150 + index * 90}ms` }}
                className="animate-fade-in-up bg-light-bg rounded-xl md:rounded-2xl border border-rose/10 p-4 md:p-5 text-center"
              >
                <dt className="font-montserrat-alt text-xs md:text-sm text-mauve">{item.label}</dt>
                <dd className="mt-1 font-alumni text-2xl md:text-4xl font-bold text-deep">
                  {item.value}
                  <span className="ml-1 font-montserrat-alt text-sm md:text-base font-normal text-mauve">
                    {t.macrosUnit}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div
        style={{ animationDelay: '220ms' }}
        className="animate-fade-in-up bg-white rounded-2xl border border-rose/10 p-5 md:p-8"
      >
        <h3 className="font-alumni text-lg md:text-2xl font-semibold text-deep">{t.limitationsHeading}</h3>
        <p className="mt-2 font-montserrat-alt text-sm text-deep/80 leading-relaxed">{t.limitationsLead}</p>
        <p className="mt-4 font-montserrat-alt text-sm text-deep/80 leading-relaxed">{t.limitationsFactorsLead}</p>
        <ul className="mt-3 space-y-2">
          {t.limitationsFactors.map((factor) => (
            <li key={factor} className="flex items-start gap-2 font-montserrat-alt text-sm text-deep/80">
              <svg className="w-4 h-4 mt-0.5 shrink-0 text-purple" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>{factor}</span>
            </li>
          ))}
        </ul>
      </div>

      <div
        style={{ animationDelay: '320ms' }}
        className="animate-fade-in-up bg-gradient-warm rounded-2xl md:rounded-3xl p-6 md:p-10 text-center"
      >
        <h3 className="font-alumni text-2xl md:text-4xl font-bold text-deep">{t.ctaHeading}</h3>
        <p className="mt-3 font-montserrat-alt text-sm md:text-base text-deep/85 leading-relaxed max-w-xl mx-auto">
          {t.ctaBody}
        </p>
        <Link
          href="/racion"
          data-gtag-event="calculator_ration_cta"
          className="mt-6 inline-flex items-center gap-2 font-montserrat-alt font-semibold min-h-[52px] px-8 py-4 text-base rounded-full bg-rose text-deep hover:bg-cream hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
        >
          {t.ctaLabel}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
          </svg>
        </Link>
      </div>
    </section>
  )
}
